import React, { useEffect, useState } from "react";
import { Activity, MessageSquareWarning, Star } from "lucide-react";
import { api } from "../api/client";

const COLORS = {
    positive: { bg: "bg-emerald-500", text: "text-emerald-700", soft: "bg-emerald-100" },
    neutral: { bg: "bg-zinc-400", text: "text-zinc-600", soft: "bg-zinc-100" },
    negative: { bg: "bg-amber-500", text: "text-amber-700", soft: "bg-amber-100" },
    urgent: { bg: "bg-rose-500", text: "text-rose-700", soft: "bg-rose-100" },
};

const Bar = ({ label, count, total, color }) => {
    const pct = total > 0 ? (count / total) * 100 : 0;
    return (
        <div className="space-y-1" data-testid={`sentiment-bar-${label}`}>
            <div className="flex items-center justify-between text-xs">
                <span className={`font-medium capitalize ${color.text}`}>{label}</span>
                <span className="font-semibold text-zinc-700">{count}</span>
            </div>
            <div className={`h-1.5 w-full overflow-hidden rounded-full ${color.soft}`}>
                <div className={`h-full ${color.bg}`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
};

const SentimentRadarCard = () => {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(7);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/admin/dashboard/sentiment-radar", { params: { days } });
                if (mounted) setData(data);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [days]);

    if (loading) {
        return (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="h-32 skeleton rounded-xl" />
            </div>
        );
    }
    if (!data) return null;

    const total = data.total || 0;
    const by = data.by_label || {};
    return (
        <div
            className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
            data-testid="sentiment-radar-card"
        >
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Sentiment Radar</p>
                    <h3 className="mt-0.5 flex items-center gap-1.5 text-base font-semibold text-slate-900">
                        <Activity className="h-4 w-4 text-brand-600" />
                        Customer pulse — last {data.window_days || days}d
                    </h3>
                </div>
                <select
                    value={days}
                    onChange={(e) => setDays(Number(e.target.value))}
                    className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700"
                    data-testid="sentiment-radar-window"
                >
                    <option value={1}>1d</option>
                    <option value={7}>7d</option>
                    <option value={30}>30d</option>
                </select>
            </div>

            {total === 0 ? (
                <p className="text-sm text-slate-500" data-testid="sentiment-radar-empty">
                    No analyzed interactions in this window.
                </p>
            ) : (
                <>
                    <div className="mb-3 grid grid-cols-2 gap-3 text-center text-xs">
                        <div className="rounded-xl bg-slate-50 p-2.5" data-testid="sentiment-stat-total">
                            <p className="text-2xl font-semibold text-slate-900">{total}</p>
                            <p className="text-[10px] uppercase tracking-wider text-slate-500">Signals</p>
                        </div>
                        <div className="rounded-xl bg-rose-50 p-2.5" data-testid="sentiment-stat-escalations">
                            <p className="flex items-center justify-center gap-1 text-2xl font-semibold text-rose-700">
                                <MessageSquareWarning className="h-5 w-5" /> {data.escalations || 0}
                            </p>
                            <p className="text-[10px] uppercase tracking-wider text-rose-600">Escalations</p>
                        </div>
                    </div>

                    <div className="space-y-2.5">
                        {["positive", "neutral", "negative", "urgent"].map((lab) => (
                            <Bar key={lab} label={lab} count={by[lab] || 0} total={total} color={COLORS[lab]} />
                        ))}
                    </div>

                    <div className="mt-4 grid grid-cols-3 gap-2 border-t border-slate-100 pt-3 text-center text-[10px] uppercase tracking-wider text-slate-500">
                        <div data-testid="sentiment-source-ticket">Tickets <span className="ml-1 font-semibold text-slate-700">{data.by_source?.ticket || 0}</span></div>
                        <div data-testid="sentiment-source-chat">AI chats <span className="ml-1 font-semibold text-slate-700">{data.by_source?.chat || 0}</span></div>
                        <div data-testid="sentiment-source-review">Reviews <span className="ml-1 font-semibold text-slate-700">{data.by_source?.review || 0}</span></div>
                    </div>
                </>
            )}
        </div>
    );
};

export default SentimentRadarCard;
